import { useRouter } from "next/router";
import type React from "react";
import { useRef } from "react";
import { authPaths, useAuth } from "@medbillai/app/lib/auth";
import { useSignUpOnboardingStore } from "@medbillai/app/lib/sign-up-onboarding/signUpOnboardingStore";
import { anonymousSessionStorage } from "@medbillai/app/lib/storage";
import { onboardingGate } from "@medbillai/app/screens/onboarding-v3/utils/onboardingGate";
import { FullscreenSpinner } from "@medbillai/ui";
import { buildUrlWithParams, homeUrl, loginUrl, onboardingUrl } from "@medbillai/utils/urls";
export const AuthGate = (props: {
  children: React.ReactNode;
}) => {
  const {
    isReady,
    isAuthenticated,
    setIntendedPath,
    user
  } = useAuth();
  const {
    replace,
    pathname,
    query,
    isReady: routerIsReady
  } = useRouter();
  const redirectedRef = useRef(false);
  const {
    setIsSignUpOnboarding
  } = useSignUpOnboardingStore();
  if (!isReady || !routerIsReady) {
    return <FullscreenSpinner />;
  }
  if (!isAuthenticated) {
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      const anonToken = typeof query.anonToken === "string" ? query.anonToken : null;
      if (anonToken) {
        void anonymousSessionStorage.set("token", anonToken);
        setIsSignUpOnboarding(true);
      }

      // Only store paths that aren't auth-related and aren't the home page
      const shouldStorePath = pathname !== "/" && !authPaths.includes(pathname);
      if (shouldStorePath) {
        const {
          anonToken: _,
          ...rest
        } = query;
        setIntendedPath(buildUrlWithParams(pathname, rest));
      }
      void replace(loginUrl());
    }
    return <FullscreenSpinner />;
  }
  if (onboardingGate({
    user,
    pathname
  }) === "resume") {
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      // The gate interrupts the journey, it must not erase it: completion
      // consumes the stash, so a deep link lands where it promised after
      // onboarding instead of at home.
      const shouldStorePath = pathname !== "/" && !authPaths.includes(pathname);
      if (shouldStorePath) {
        setIntendedPath(buildUrlWithParams(pathname, query));
      }
      void replace(onboardingUrl());
    }
    return <FullscreenSpinner />;
  }

  // If authenticated and on an auth page, redirect to home
  if (isAuthenticated && authPaths.includes(pathname)) {
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      void replace(homeUrl());
    }
    return <FullscreenSpinner />;
  }
  return <>{props.children}</>;
};